import {
  FormField,
  SelectProps,
  Multiselect,
  NonCancelableCustomEvent,
  MultiselectProps,
  TextContent,
} from '@amzn/awsui-components-core';
import { Component } from 'react';
import React from 'react';
import constants from '../../../common/constants';
import InfoLink from '../../common/InfoLink';

interface Props {
  onSelectsUpdated?: (sectionId: string, selectedIds: string[], validationError?: string) => void;
  onValidationError?: (validationError?: string) => void;
  sectionId?: string;
  label: string;
  description?: string;
  contextualHelp?: string;
  readonly?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  showValidationError?: boolean;
  selectedIds: string[];
  selectOptions: SelectProps.Option[];
  stretch?: boolean;
  placeholder?: string;
  filtering?: boolean;
  maxNumber?: number;
}

interface State {
  selectedOptions: SelectProps.Option[];
  validationError?: string;
}

class MetadataMultiselectInput extends Component<Props, State> {
  static defaultProps = {
    description: '',
    disabled: false,
    filtering: true,
    isRequired: false,
    placeholder: '',
    readonly: false,
    showValidationError: true,
    stretch: false,
    maxNumber: 0,
  };

  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    // @ts-expect-error TS7006 - strict migration
    const selectedIds = (props.selectedIds || []).filter((i) => i != null);
    const validationError = this.validateInput(selectedIds);
    const selectedOptions = props.selectOptions.filter(
      // @ts-expect-error TS7006 - strict migration
      (option) => option.value && selectedIds?.includes(option.value),
    );
    this.state = {
      selectedOptions,
      validationError,
    };
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    const selectedValues = this.state.selectedOptions
      .map((option) => option.value)
      .filter((option) => option !== undefined);
    if (
      this.props.selectedIds !== prevProps.selectedIds &&
      this.props.selectedIds !== selectedValues
    ) {
      const selectedIds = (this.props.selectedIds || []).filter((i) => i != null);
      const selectedOptions = this.props.selectOptions.filter(
        (option) => option.value && selectedIds?.includes(option.value),
      );
      this.setState({
        selectedOptions,
      });
      this.validateInput(selectedIds);
    }

    if (prevProps.showValidationError && !this.props.showValidationError) {
      this.setState({
        validationError: undefined,
      });
    }
  }

  public render() {
    const {
      readonly,
      label,
      description,
      contextualHelp,
      stretch,
      disabled,
      selectOptions,
      placeholder,
      isRequired,
      filtering,
      showValidationError,
    } = this.props;

    const { selectedOptions, validationError } = this.state;
    if (readonly) {
      const displayMetatada = (
        <details open>
          <summary>{label}</summary>
          <TextContent>
            <ul>
              {selectedOptions.map((selectedOption, idx) => (
                <li key={selectedOption.value}>
                  {' '}
                  {this.getItemLabel(selectedOption.value ?? '')}{' '}
                </li>
              ))}
            </ul>
          </TextContent>
        </details>
      );
      return selectedOptions.length ? displayMetatada : null;
    } else {
      return (
        <FormField
          errorText={showValidationError && validationError}
          label={
            <span>
              {label}
              {!isRequired && <i> - optional</i>}
            </span>
          }
          info={contextualHelp && <InfoLink topic={contextualHelp} />}
          description={description}
          stretch={stretch}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          <Multiselect
            selectedOptions={selectedOptions}
            onChange={this.handleInput}
            options={selectOptions}
            placeholder={placeholder}
            disabled={disabled}
            filteringType={filtering ? 'auto' : 'none'}
            keepOpen={false}
            deselectAriaLabel={() => 'Remove option'}
            ariaRequired={isRequired}
          />
        </FormField>
      );
    }
  }

  private handleInput = (
    event: NonCancelableCustomEvent<MultiselectProps.MultiselectChangeDetail>,
  ) => {
    const values: string[] = event.detail.selectedOptions
      ?.map((option) => option.value)
      .filter((option) => option !== undefined);
    const error = this.validateInput(values);

    this.setState({
      selectedOptions: [...event.detail.selectedOptions],
      validationError: error,
    });
    this.updateSectionValues(values, error);
  };

  private validateInput = (value: string[]) => {
    const { isRequired, onValidationError, maxNumber } = this.props;

    let error: string;

    // @ts-expect-error TS18048 - strict migration
    if (maxNumber > 0 && value.length > maxNumber) {
      error = constants.MAX_SERVICES_EXCEEDED;
    }

    if (isRequired && value.length === 0) {
      error = constants.REQUIRED;
    }

    if (onValidationError) {
      // @ts-expect-error TS2454 - strict migration
      onValidationError(error);
    }

    // @ts-expect-error TS2454 - strict migration
    return error;
  };

  private getItemLabel = (id: string) => {
    const { selectOptions } = this.props;
    const item = selectOptions.find((o) => o.value === id);
    return item ? item.label : '';
  };

  private updateSectionValues = (values: string[], error?: string) => {
    const { sectionId, onSelectsUpdated: onUpdated } = this.props;
    if (sectionId && onUpdated) {
      onUpdated(sectionId, values, error);
    }
  };
}

export default MetadataMultiselectInput;
